import { css, SerializedStyles } from '@emotion/react';
import { getSizeValue } from '../theming';
import { ToastPosition } from './toasts.store';

// Static styles
export const STATIC_STYLES: SerializedStyles = css`
    width: var(--toast-container-width);
    position: var(--toasts-position);
    z-index: var(--hc-z-index-toast);
    max-width: var(--toast-container-max-width);

    &:where([data-position='top-center']) {
        top: var(--toasts-margin);
        inset-inline-start: 50%;
        transform: translateX(-50%);
    }

    &:where([data-position='top-start']) {
        top: var(--toasts-margin);
        inset-inline-start: var(--toasts-margin);
    }

    &:where([data-position='top-end']) {
        top: var(--toasts-margin);
        inset-inline-end: var(--toasts-margin);
    }

    &:where([data-position='bottom-center']) {
        bottom: var(--toasts-margin);
        inset-inline-start: 50%;
        transform: translateX(-50%);
    }

    &:where([data-position='bottom-start']) {
        bottom: var(--toasts-margin);
        inset-inline-start: var(--toasts-margin);
    }

    &:where([data-position='bottom-end']) {
        bottom: var(--toasts-margin);
        inset-inline-end: var(--toasts-margin);
    }

    > [data-transition-group] {
        display: flex;
        flex-direction: column;
    }

    &:where([data-position$='center']) {
        > [data-transition-group] {
            align-items: center;
        }
    }

    &:where([data-position$='start']) {
        > [data-transition-group] {
            align-items: flex-start;
        }
    }

    &:where([data-position$='end']) {
        > [data-transition-group] {
            align-items: flex-end;
        }
    }

    [data-toast] + [data-toast] {
        margin-top: calc(var(--hc-spacing-unit) * 3);
    }

    [data-toast] {
        width: var(--toast-width);
        opacity: 0;
        max-height: var(--toast-max-height);

        transform: var(--toast-transform-out);

        transition-duration: var(--toast-transition-duration), var(--toast-transition-duration),
            var(--toast-transition-duration);
        transition-timing-function: cubic-bezier(0.51, 0.3, 0, 1.21), cubic-bezier(0.51, 0.3, 0, 1.21), linear;
        transition-property: opacity, transform, max-height;

        &:where([data-state='entering'], [data-state='entered']) {
            opacity: 1;
            transform: var(--toast-transform-in);
        }

        &:where([data-state='exiting'], [data-state='exited']) {
            opacity: 0;
            max-height: 0;
            transform: var(--toast-transform-out);
        }
    }
`;

export function useContainerDynamicStyles(
    toastMaxWidth: number | string,
    toastMaxHeight: number | string,
    toastWidth: 'fill' | 'auto',
    transitionDuration: number,
    position: 'fixed' | 'absolute'
): React.CSSProperties {
    return {
        '--toast-container-max-width': getSizeValue(toastMaxWidth),
        '--toast-container-width': toastWidth === 'fill' ? 'calc(100% - var(--toasts-margin) * 2)' : 'auto',
        '--toast-width': toastWidth === 'fill' ? '100%' : 'auto',
        '--toast-max-height': getSizeValue(toastMaxHeight),
        '--toast-transition-duration': `${transitionDuration}ms`,
        '--toasts-position': position,
        '--toasts-margin': position === 'fixed' ? 'calc(var(--hc-spacing-unit) * 3)' : '0px',
    } as React.CSSProperties;
}

const inTransform = {
    start: 'translateX(0)',
    end: 'translateX(0)',
    'top-center': 'translateY(0)',
    'bottom-center': 'translateY(0)',
};

const outTransform = {
    start: 'translateX(-100%)',
    end: 'translateX(100%)',
    'top-center': 'translateY(-100%)',
    'bottom-center': 'translateY(100%)',
};

export function useToastDynamicStyles(position: ToastPosition): React.CSSProperties {
    const [vertical, horizontal] = position.split('-');
    const property = (horizontal === 'center' ? `${vertical}-center` : horizontal) as keyof typeof inTransform;

    return {
        '--toast-transform-in': inTransform[property],
        '--toast-transform-out': outTransform[property],
    } as React.CSSProperties;
}
